<script lang="ts" setup>
import { Bars3Icon } from "@heroicons/vue/24/solid";
import { computed } from "vue";
import { useAuth } from "~/composables/useAuth";
import { useDisplay } from "@/composables/useDisplay";
import NavigationMobile from "~/components/Navigation/Mobile.vue";

// Using the global React Native detection plugin instead of window object check

// Use the OnePass Mobile logo
// Use ~/ prefix to ensure proper asset resolution
const logoSrc = "/images/OnePass_Mobile_Logo_Long_Rev_RGB.webp";

// Add computed property for assets base URL to help with debugging
const assetBaseUrl = computed(() => {
  // This will help us understand if there are path resolution issues
  console.log("Asset base URL info:", {
    publicBase: "/",
  });
  return "/";
});

// Explicit path for brand SVG images to ensure they load properly
const imageBasePath = "/icons/brands/";
const logoAlt = "OnePass Mobile";
const logoRole = "logo";

const mainStore = useMainStore();
const { showMobileNavigation, globalSettings } = storeToRefs(mainStore);

const navigation = computed(() => globalSettings.value?.header);

// Use the display composable for consistent mobile detection
const { isMobile } = useDisplay();

// Initialize isReactNative ref as false to maintain SSR/CSR consistency
const isReactNative = ref(false);

// Create a unified computed property to determine header visibility
// This ensures consistent rendering between server and client
const showHeader = computed(() => {
  // During SSR, always render a consistent state
  if (import.meta.server) return false;
  return !isReactNative.value;
});

// More detailed logging of the navigation items from Strapi
if (!import.meta.server && navigation.value) {
  console.log("Header navigation items (raw):", navigation.value);
  console.log("Header navigation items count:", navigation.value.length);
  console.log("Header navigation items detail:", JSON.stringify(navigation.value, null, 2));
}

const { status, user } = useAuth();
const loggedIn = computed(() => status.value === "authenticated");

// Only ONE active dropdown at a time
const activeDropdown = ref("");

// Track whether a dropdown is currently opened
const isDropdownOpen = computed(() => activeDropdown.value !== "");

// Simple function to show a dropdown
function showDropdown(which: string, event?: MouseEvent): void {
  if (event) {
    event.stopPropagation();
    event.preventDefault();
  }

  // If this dropdown is already open, close it
  if (activeDropdown.value === which) {
    activeDropdown.value = "";
    return;
  }

  // Hide any open dropdown
  hideAllDropdowns();

  // Then show the selected one
  activeDropdown.value = which;
}

// Hide all dropdowns
function hideAllDropdowns(): void {
  activeDropdown.value = "";
}

onMounted(() => {
  // Initialize React Native detection client-side only
  const { $reactNative } = useNuxtApp();
  isReactNative.value = $reactNative?.isApp || false;
  console.log("Header: Using React Native plugin detection:", isReactNative.value);
  
  // Handle clicks anywhere on the page to close dropdowns
  document.addEventListener("click", (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't close dropdown if clicking on menu item or inside dropdown
    const clickedOnMenuItem = event.target.closest(".menu-toggle") !== null;
    const clickedInsideDropdown =
      event.target.closest(".dropdown-menu") !== null;

    if (!clickedOnMenuItem && !clickedInsideDropdown) {
      hideAllDropdowns();
    }
  });
});

onUnmounted(() => {
  // Clean up event listeners
  document.removeEventListener("click", hideAllDropdowns);
});
</script>

<template>
  <!-- Only the purple header is fixed -->
  <!-- Hide the entire header when in React Native app -->
  <!-- Use ClientOnly to ensure consistent hydration between server and client -->
  <ClientOnly>
    <header
      v-if="showHeader"
      class="fixed top-0 inset-x-0 bg-[#9900F1] shadow-md h-[80px] z-[9000] onepass-header rounded-b-[20px]"
      style="isolation: isolate; background-color: #9900f1 !important"
  >
    <div class="container mx-auto px-4 h-full">
      <div class="flex justify-between items-center h-full">
        <!-- Logo at left edge -->
        <div class="flex-shrink-0">
          <nuxt-link to="/">
            <img :src="logoSrc" :alt="logoAlt" :role="logoRole" class="h-8" />
          </nuxt-link>
        </div>

        <!-- Navigation items - Direct large text implementation for desktop ONLY -->
        <div
          class="hidden lg:flex flex-1 justify-center h-full"
          style="overflow: visible"
        >
          <nav
            class="h-full relative"
            style="isolation: isolate; z-index: 9500"
          >
            <ul
              class="flex items-center justify-center h-full space-x-6 m-0 p-0"
            >
              <!-- Dynamic navigation items from CMS wrapped in ClientOnly -->
              <ClientOnly>
                <template v-if="navigation && navigation.length > 0">
                  <li
                    v-for="(item, index) in navigation"
                    :key="item.id"
                    :class="{'px-2': true, 'menu-item-with-dropdown': item.subitems && item.subitems.length > 0}"
                  >
                    <!-- Debug information to trace rendering -->
                    <span style="display: none;">{{ console.log('Rendering nav item', index, item.title) }}</span>
                  <!-- Menu items with dropdown -->
                  <template v-if="item.subitems && item.subitems.length > 0">
                    <a
                      href="#"
                      class="text-white hover:text-white/90 flex items-center gap-1 menu-toggle"
                      style="
                        font-size: 14px !important;
                        font-weight: 700 !important;
                        letter-spacing: 0.5px;
                      "
                      @click.prevent="showDropdown(item.id.toString())"
                    >
                      {{ item.title }}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-4 w-4 text-white"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </a>
                    <div
                      v-if="activeDropdown === item.id.toString()"
                      class="dropdown-menu"
                      style="
                        position: absolute !important;
                        z-index: 10000 !important;
                        background-color: #9900f1;
                        box-shadow: 0 8px 16px rgba(0, 0, 0, 0.5);
                      "
                    >
                      <template v-for="subitem in item.subitems" :key="subitem.id">
                        <!-- Handle different link types -->
                        <a 
                          v-if="subitem.type === 'custom'" 
                          :href="subitem.url"
                          :target="subitem.target || '_self'"
                        >
                          {{ subitem.linkText || subitem.title }}
                        </a>
                        <a 
                          v-else-if="subitem.type === 'url'" 
                          :href="subitem.url?.startsWith('http') ? subitem.url : '/' + subitem.url"
                          :target="subitem.target === 'blank' ? '_blank' : (subitem.target || '_self')"
                        >
                          {{ subitem.linkText || subitem.title }}
                        </a>
                        <a 
                          v-else-if="subitem.type === 'page' && subitem.page" 
                          :href="'/' + subitem.page.slug"
                        >
                          {{ subitem.linkText || subitem.page.title }}
                        </a>
                      </template>
                    </div>
                  </template>
                  
                  <!-- 
                    Simple menu items without dropdown 
                    
                    Strapi CMS provides navigation items with different link types:
                    1. 'custom': Custom links with user-defined URL and target
                    2. 'url': URL-based links that can be relative or absolute
                    3. 'page': Links to Strapi CMS pages with slug-based routing
                    
                    Each link type requires specific handling for href generation and target attributes.
                    We must handle all possible link types to ensure all items from Strapi render correctly.
                  -->
                  <template v-else-if="item.link">
                    <!-- Handle different link types from Strapi CMS -->
                    <a
                      v-if="item.link.type === 'custom'"
                      :href="item.link.url"
                      :target="item.link.target || '_self'"
                      class="text-white hover:text-white/90"
                      style="
                        font-size: 14px !important;
                        font-weight: 700 !important;
                        letter-spacing: 0.5px;
                      "
                    >
                      {{ item.link.linkText || item.title }}
                    </a>
                    <!-- 
                      Handle URL type links from Strapi 
                      - URL type links can be relative paths or absolute URLs
                      - We need to check if the URL starts with 'http' to determine if it needs a leading '/'
                      - For target attribute, Strapi uses 'blank' instead of '_blank', so we convert it
                      - This handles items like "Activate your SIM", "Recharge", and "Membership"
                    -->
                    <a
                      v-else-if="item.link.type === 'url'"
                      :href="item.link.url?.startsWith('http') ? item.link.url : '/' + item.link.url"
                      :target="item.link.target === 'blank' ? '_blank' : (item.link.target || '_self')"
                      class="text-white hover:text-white/90"
                      style="
                        font-size: 14px !important;
                        font-weight: 700 !important;
                        letter-spacing: 0.5px;
                      "
                    >
                      {{ item.link.linkText || item.title }}
                    </a>
                    <a
                      v-else-if="item.link.type === 'page' && item.link.page"
                      :href="'/' + item.link.page.slug"
                      class="text-white hover:text-white/90"
                      style="
                        font-size: 14px !important;
                        font-weight: 700 !important;
                        letter-spacing: 0.5px;
                      "
                    >
                      {{ item.link.linkText || item.link.page.title || item.title }}
                    </a>
                  </template>
                  </li>
                </template>

                <!-- Fallback hardcoded navigation if CMS data is missing -->
                <template v-else>
                <!-- Plans dropdown with click implementation -->
                <li class="px-2 menu-item-with-dropdown">
                  <a
                    href="#"
                    class="text-white hover:text-white/90 flex items-center gap-1 menu-toggle"
                    style="
                      font-size: 14px !important;
                      font-weight: 700 !important;
                      text-transform: uppercase;
                      letter-spacing: 0.5px;
                    "
                    @click.prevent="showDropdown('plans')"
                  >
                    PLANS
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-4 w-4 text-white"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </a>
                  <div
                    v-if="activeDropdown === 'plans'"
                    class="dropdown-menu plans-dropdown"
                    style="
                      position: absolute !important;
                      z-index: 10000 !important;
                      background-color: #9900f1;
                      box-shadow: 0 8px 16px rgba(0, 0, 0, 0.5);
                    "
                  >
                    <a href="/plans/mobile">Mobile plans</a>
                    <a href="/extra-packs">Extra Packs</a>
                  </div>
                </li>

                <!-- Activate your SIM -->
                <li class="px-2">
                  <a
                    href="/activate-your-sim"
                    class="text-white hover:text-white/90"
                    style="
                      font-size: 14px !important;
                      font-weight: 700 !important;
                      text-transform: uppercase;
                      letter-spacing: 0.5px;
                    "
                  >
                    ACTIVATE
                  </a>
                </li>

                <!-- Recharge -->
                <li class="px-2">
                  <a
                    href="/recharge"
                    class="text-white hover:text-white/90"
                    style="
                      font-size: 14px !important;
                      font-weight: 700 !important;
                      text-transform: uppercase;
                      letter-spacing: 0.5px;
                    "
                  >
                    RECHARGE
                  </a>
                </li>

                <!-- Network dropdown with click implementation -->
                <li class="px-2 menu-item-with-dropdown">
                  <a
                    href="#"
                    class="text-white hover:text-white/90 flex items-center gap-1 menu-toggle"
                    style="
                      font-size: 14px !important;
                      font-weight: 700 !important;
                      text-transform: uppercase;
                      letter-spacing: 0.5px;
                    "
                    @click.prevent="showDropdown('network')"
                  >
                    NETWORK
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-4 w-4 text-white"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </a>
                  <div
                    v-if="activeDropdown === 'network'"
                    class="dropdown-menu network-dropdown"
                    style="
                      position: absolute !important;
                      z-index: 10000 !important;
                      background-color: #9900f1;
                      box-shadow: 0 8px 16px rgba(0, 0, 0, 0.5);
                    "
                  >
                    <a href="/network/coverage">Network Coverage</a>
                    <a href="/network/status">Network Status</a>
                  </div>
                </li>

                <!-- About Us -->
                <li class="px-2">
                  <a
                    href="/about-us"
                    class="text-white hover:text-white/90"
                    style="
                      font-size: 14px !important;
                      font-weight: 700 !important;
                      text-transform: uppercase;
                      letter-spacing: 0.5px;
                    "
                  >
                    ABOUT US
                  </a>
                </li>

                <!-- Help dropdown with click implementation -->
                <li class="px-2 menu-item-with-dropdown">
                  <a
                    href="#"
                    class="text-white hover:text-white/90 flex items-center gap-1 menu-toggle"
                    style="
                      font-size: 14px !important;
                      font-weight: 700 !important;
                      text-transform: uppercase;
                      letter-spacing: 0.5px;
                    "
                    @click.prevent="showDropdown('help')"
                  >
                    HELP
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-4 w-4 text-white"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </a>
                  <div
                    v-if="activeDropdown === 'help'"
                    class="dropdown-menu help-dropdown"
                    style="
                      position: absolute !important;
                      z-index: 10000 !important;
                      background-color: #9900f1;
                      box-shadow: 0 8px 16px rgba(0, 0, 0, 0.5);
                    "
                  >
                    <a href="/help/faq">FAQ</a>
                    <a href="/help/legal-regulatory">Legal & Regulatory</a>
                  </div>
                </li>
                </template>
              </ClientOnly>
            </ul>
          </nav>
        </div>

        <!-- Login buttons on far right -->
        <div class="flex items-center justify-end gap-3 flex-shrink-0">
          <!-- Standard buttons with OnePass Mobile styling -->
          <div class="hidden lg:flex items-center gap-3">
            <a
              v-if="loggedIn"
              href="/account"
              class="bg-white text-[#9900F1] rounded-full py-2.5 px-8 text-lg font-bold hover:bg-white/90 transition-colors"
            >
              My Account
            </a>
            <a
              v-else
              href="/account"
              class="bg-white text-[#9900F1] rounded-full py-2.5 px-8 text-lg font-bold hover:bg-white/90 transition-colors"
            >
              Log in
            </a>

            <!-- Cart icon with proper styling -->
            <SharedMiniCart class="ml-2" />
          </div>

          <!-- Mobile navigation area (split to allow selective hiding) -->
          <div
            v-if="!isReactNative"
            class="lg:hidden flex items-center gap-2"
          >
            <!-- Mobile login/account button (hidden on mobile webview) -->
            <a
              v-if="!isMobile"
              href="/account"
              class="bg-white text-[#9900F1] rounded-full py-1.5 px-5 text-sm font-bold hover:bg-white/90 transition-colors"
            >
              {{ loggedIn ? "My Account" : "Log in" }}
            </a>

            <!-- Mobile menu button always visible with higher z-index -->
            <button
              aria-label="Menu"
              class="focus:outline-none relative z-[9500]"
              @click="mainStore.toggleMobileNavigation"
            >
              <Bars3Icon class="h-10 w-10 text-white" />
            </button>
          </div>
          <!-- Cart only shown on mobile here since we moved it to the right nav for desktop -->
        </div>
      </div>
    </div>
  </header>
  </ClientOnly>

  <!-- Spacer div for the fixed header, only shown when header is visible -->
  <ClientOnly>
    <div v-if="showHeader" class="header-spacer"></div>
  </ClientOnly>

  <!-- Mobile Navigation with correct component path -->
  <Transition name="slide-from-right">
    <Navigation-Mobile v-if="showMobileNavigation" />
  </Transition>
</template>
<style>
/* Global styles for page structure */
:global(body) {
  overflow-x: hidden;
}

/* Header base styles */
:global(.onepass-header) {
  background-color: #9900f1 !important;
  position: relative;
  z-index: 9000;
  overflow: visible !important;
}

:global(.menu-item-with-dropdown) {
  position: relative;
  overflow: visible !important;
}

:global(.dropdown-menu) {
  z-index: 10000 !important;
  position: absolute !important;
}
</style>

<style scoped>
/* Import custom header styles */
@import "~/assets/css/header-custom.css";

/* Base header styles */
header {
  background-color: #9900f1 !important;
}

/* Responsive header styling */
@media (max-width: 1023px) {
  /* Mobile: No rounded corners */
  .onepass-header {
    border-radius: 0 !important;
  }

  header.rounded-b-\[20px\] {
    border-radius: 0 !important;
  }
}

@media (min-width: 1024px) {
  /* Desktop: Show rounded corners */
  .onepass-header {
    border-bottom-left-radius: 20px !important;
    border-bottom-right-radius: 20px !important;
  }
}

/* Direct override for nav text size */
:deep(.nav-link) {
  font-size: 24px !important;
  font-weight: 700 !important;
  letter-spacing: 0.5px;
}

/* Header spacer - just for the purple header height */
.header-spacer {
  height: 80px; /* Only the header height - partner section scrolls normally */
}

/* Menu item with dropdown */
.menu-item-with-dropdown {
  position: relative;
  isolation: isolate;
}

/* Dropdown menu styling */
.dropdown-menu {
  position: absolute;
  width: 200px;
  background-color: #9900f1;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.5);
  border-radius: 4px;
  z-index: 9999 !important;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 5px;
  overflow: hidden;
}

/* Individual dropdown positioning adjustments */
.plans-dropdown {
  margin-left: -15px;
}

.network-dropdown {
  margin-left: 0px;
}

.help-dropdown {
  margin-left: 0px;
}

.dropdown-menu a {
  display: block;
  padding: 12px 16px;
  color: white !important;
  font-size: 12px;
  text-transform: uppercase;
  font-weight: 600;
  text-decoration: none;
  transition: background-color 0.2s;
}

.dropdown-menu a:hover {
  background-color: #8800de;
}
</style>
